import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Pill, SelectableTile, Tooltip, Typography } from "@aviary";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { useMFAData } from "@shared/hooks/useMFAData/useMFAData";
import { l } from "@shared/locales/i18n";
import { TwoFactorAuthenticationMethods as AuthMethod } from "@shared/types/graphqlGenerated";

interface Props {
  selectedAuthMethod: AuthMethod;
  onSelect: (event: ChangeEvent<HTMLInputElement>) => void;
}

const ChooseAuthenticationMethod = ({ selectedAuthMethod, onSelect }: Props) => {
  const [isEmailMFAEnabled] = useFlippers("iam_email_mfa");
  const { t } = useTranslation();
  const { isMFAEnabled, otpMethod } = useMFAData();

  const isEmailDisabled = isMFAEnabled && otpMethod === AuthMethod.Email;

  const renderAuthMethod = (method: AuthMethod) => {
    switch (method) {
      case AuthMethod.Sms:
        return (
          <SelectableTile
            aria-label={t(l.mfa.chooseAuthenticationMethod.useTextMessage)}
            name={AuthMethod.Sms}
            onChange={onSelect}
            checked={selectedAuthMethod === AuthMethod.Sms}
            value={AuthMethod.Sms}
            isRadio
            isInline
            isContentHeight
          >
            <Typography type="h5" isMarginless>
              {t(l.mfa.chooseAuthenticationMethod.useTextMessage)}
            </Typography>
            <Typography>
              {t(l.mfa.chooseAuthenticationMethod.receiveOneTimeCodeViaTextMessage)}
            </Typography>
          </SelectableTile>
        );
      case AuthMethod.Email:
        return (
          <SelectableTile
            aria-label={t(l.mfa.chooseAuthenticationMethod.useEmail)}
            name={AuthMethod.Email}
            onChange={onSelect}
            checked={selectedAuthMethod === AuthMethod.Email}
            value={AuthMethod.Email}
            disabled={isEmailDisabled}
            isRadio
            isInline
            isContentHeight
          >
            <Tooltip
              tooltipContent={t(l.mfa.chooseAuthenticationMethod.emailMFAAlreadyEnabled)}
              textDirection="left"
              isTooltipHidden={!isEmailDisabled}
            >
              <Arrange spacing="none">
                <Typography type="h5" isMarginless>
                  {t(l.mfa.chooseAuthenticationMethod.useEmail)}
                </Typography>
                <Typography>
                  {t(l.mfa.chooseAuthenticationMethod.receiveOneTimeCodeViaEmail)}
                </Typography>
              </Arrange>
            </Tooltip>
          </SelectableTile>
        );
      case AuthMethod.App:
        return (
          <SelectableTile
            aria-label={t(l.mfa.chooseAuthenticationMethod.useAuthenticatorApp)}
            name={AuthMethod.App}
            onChange={onSelect}
            checked={selectedAuthMethod === AuthMethod.App}
            value={AuthMethod.App}
            isRadio
            isInline
            isContentHeight
          >
            <Arrange alignment="center" spacing="xxsmall">
              <Typography type="h5">
                {t(l.mfa.chooseAuthenticationMethod.useAuthenticatorApp)}
              </Typography>
              <Pill size="small" isColor="system">
                {t(l.mfa.chooseAuthenticationMethod.moreSecure)}
              </Pill>
            </Arrange>
            <Typography>
              {t(l.mfa.chooseAuthenticationMethod.generateCodeViaAuthenticatorApp)}
            </Typography>
          </SelectableTile>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderAuthMethod(AuthMethod.Sms)}
      {isEmailMFAEnabled && renderAuthMethod(AuthMethod.Email)}
      {renderAuthMethod(AuthMethod.App)}
    </>
  );
};

export { ChooseAuthenticationMethod };
